// Utility for handling error messages with i18n support
// This allows services to use translated error messages
import i18n from '@/i18n';

function getCurrentLocale() {
  try {
    const locale = i18n?.global?.locale;
    return (locale?.value ?? locale ?? undefined) || 'en';
  } catch {
    return 'en';
  }
}

export const getErrorMessage = (key, fallback = 'An error occurred') => {
  // For now, return the fallback message
  // In the future, this can be enhanced to use i18n directly
  // or receive the current language as a parameter
  return fallback;
};

export const POST_ERROR_MESSAGES = {
  VERIFICATION_FAILED: 'Code verification failed',
  CANT_GET_FEED: 'Can\'t get feed',
  CANT_GET_POST: 'Unable to load post',
  CANT_GET_DRAFTS: 'Unable to load drafts',
  CANT_DELETE_POST: 'Can\'t delete post',
  FAILED_TO_PIN_POST: 'Failed to pin post',
  FAILED_TO_MARK_AS_DRAFT: 'Failed to mark post as draft',
  FAILED_TO_UPDATE_POST: 'Failed to update post',
  FAILED_TO_REPORT_POST: 'Failed to report post',
  FAILED_TO_REPORT_COMMENT: 'Failed to report comment',
  FAILED_TO_PUBLISH_DRAFT: 'Failed to publish draft',
  FAILED_TO_LOAD_EMOTIONS: 'Failed to load emotions',
  FAILED_TO_LOAD_ACTIVITIES: 'Failed to load activities',
  FAILED_TO_CREATE_EMOTION: 'Failed to create emotion',
  FAILED_TO_CREATE_ACTIVITY: 'Failed to create activity',
  POST_PINNED_SUCCESSFULLY: 'Post pinned successfully',
  POST_UPDATED_SUCCESSFULLY: 'Post updated successfully',
  ALBUM_CREATION_FAILED: 'Failed to create album',
  ALBUM_TITLE_EXISTS: 'An album with this title already exists',
  CANT_GET_ALBUMS: 'Unable to load albums',
  CANT_GET_ALBUM: 'Unable to load album',
  CANT_DELETE_ALBUM: 'Unable to delete album',
  ALBUM_ACCESS_DENIED: 'Access denied to this album',
  ALBUM_DELETE_ACCESS_DENIED: 'Access denied - You cannot delete this album',
  ALBUM_NOT_FOUND: 'Album not found',
  POST_ALREADY_IN_ALBUM: 'This post is already in the album',
  ALBUM_POST_ACCESS_DENIED: 'Access denied',
  ALBUM_OR_POST_NOT_FOUND: 'Album or post not found',
  CANT_ADD_POST_TO_ALBUM: 'Unable to add post to album',
  ALBUM_UPDATE_FAILED: 'Failed to update album',
  ALBUM_UPDATE_ACCESS_DENIED: 'Access denied - You cannot update this album',
  ALBUM_UPDATE_NOT_FOUND: 'Album not found',
  CANT_GET_ALBUM_MEDIA: 'Unable to load album media',
  CANT_REMOVE_POST_FROM_ALBUM: 'Unable to remove post from album',
  ALBUM_POST_REMOVE_ACCESS_DENIED: 'Access denied - You cannot remove this post from the album',
  // Upload-related error messages
  UPLOAD_TIMEOUT: 'Upload timed out. Please check your connection and try again.',
  FILE_TOO_LARGE: 'File size exceeds the maximum allowed limit. Please compress your files or upload smaller ones.',
  UPLOAD_PROCESSING_TIMEOUT: 'Server is taking too long to process your upload. Please try again with fewer or smaller files.',
  // Collection-related error messages
  SERVICE_UNAVAILABLE: 'Service temporarily unavailable. Please try again later.',
  CANT_GET_COLLECTIONS: 'Unable to load collections',
  COLLECTION_CREATION_FAILED: 'Failed to create collection',
  FAILED_TO_SAVE_POST: 'Failed to save post to collection',
  CANT_GET_SAVED_POSTS: 'Unable to load saved posts',
  FAILED_TO_CHECK_SAVED_POST: 'Failed to check if post is saved',
  SAVED_POST_NOT_FOUND: 'Saved post not found',
  CANT_GET_SAVED_POST: 'Unable to load saved post',
  FAILED_TO_UPDATE_SAVED_POST: 'Failed to update saved post',
  POST_UNSAVED_SUCCESS: 'Post unsaved successfully',
  FAILED_TO_UNSAVE_POST: 'Failed to unsave post',
  CANT_GET_COLLECTION_POSTS: 'Unable to load collection posts',
  FAILED_TO_UPDATE_COLLECTION: 'Failed to update collection',
  FAILED_TO_DELETE_COLLECTION: 'Failed to delete collection',
  // Event-related error messages
  CANT_GET_EVENTS: 'Unable to load events',
  FAILED_TO_DELETE_EVENT: 'Failed to delete event',
  CANT_GET_EVENT_POSTS: 'Unable to load event posts',
  CANT_GET_SUGGESTED_EVENTS: 'Unable to load suggested events',
  FAILED_TO_UPDATE_ATTENDANCE: 'Failed to update attendance status',
  FAILED_TO_LEAVE_EVENT: 'Failed to leave event',
  FAILED_TO_INVITE_FRIENDS: 'Failed to invite friends',
  CANT_GET_EVENT_ATTENDEES: 'Unable to load event attendees',
  FAILED_TO_REPORT_EVENT: 'Failed to submit report',
  FAILED_TO_ACCEPT_INVITATION: 'Failed to accept invitation',
  FAILED_TO_DECLINE_INVITATION: 'Failed to decline invitation',
  FAILED_TO_CREATE_EVENT_POST: 'Failed to create event post',
  // Education-related error messages
  CANT_GET_EDUCATIONS: 'Unable to load educations',
  FAILED_TO_CREATE_EDUCATION: 'Failed to create education',
  FAILED_TO_DELETE_EDUCATION: 'Failed to delete education',
  FAILED_TO_EDIT_EDUCATION: 'Failed to edit education',
  BAD_REQUEST: 'Bad Request - Please check all required fields are filled correctly',
  CONTEST_REQUIRED_MENTIONS: 'Your participation requires mentioning at least {requiredMentions} friend(s).',
  CONTEST_COMMENT_NOT_ADMIN: 'Only the page owner can reply to contest comments.',
  CONTEST_REQUIRE_LIKE: 'You must like the post before participating in this contest.',
  CONTEST_CANNOT_UNLIKE: 'You cannot remove your reaction while you are participating in this contest.',
  CONTEST_ENDED: 'This contest has ended. Participation is no longer accepted.',
  CONTEST_ALREADY_PARTICIPATED: 'You have already participated in this contest. Good luck!',
  CONTEST_POST_NOT_FOUND: 'Post not found.',
  CONTEST_NOT_CONTEST_POST: 'This post is not a contest.',
  CONTEST_NOT_ADMIN: 'Only the page admin can draw a winner.',
  CONTEST_NOT_ENDED: 'The contest has not ended yet.',
  CONTEST_ALREADY_DRAWN: 'A winner has already been drawn.',
  CONTEST_NO_PARTICIPANTS: 'No participants found for this contest.',
  CONTEST_ADMIN_NOT_ELIGIBLE: 'Page owners and admins cannot be winners in their own contest.',
  CONTEST_INVALID_WINNER: 'The selected user is not a valid participant in this contest.',
  CONTEST_DRAW_DATE_REQUIRED: 'Please select a draw date and time for the contest.',
  CONTEST_DRAW_DATE_INVALID: 'The draw date is invalid.',
  CONTEST_DRAW_DATE_MUST_BE_AFTER_END: 'Draw date must be on or after participation end date.',
  NOT_ALLOWED_TO_CREATE_CONTEST: 'Only the page owner can create contest posts.',
  NOT_ALLOWED_TO_UPDATE_CONTEST_FIELDS: 'Only the page owner can update contest settings.',
  CONTEST_INVALID_DRAW_MODE: 'Invalid draw mode. Use RANDOM or MOST_REACTED.',
};

// French translations
export const POST_ERROR_MESSAGES_FR = {
  VERIFICATION_FAILED: 'Échec de la vérification du code',
  CANT_GET_FEED: 'Impossible d\'obtenir le flux',
  CANT_GET_POST: 'Impossible de charger la publication',
  CANT_GET_DRAFTS: 'Impossible de charger les brouillons',
  CANT_DELETE_POST: 'Impossible de supprimer la publication',
  FAILED_TO_PIN_POST: 'Échec de l\'épinglage de la publication',
  FAILED_TO_MARK_AS_DRAFT: 'Échec de la mise en brouillon',
  FAILED_TO_UPDATE_POST: 'Échec de la mise à jour de la publication',
  FAILED_TO_REPORT_POST: 'Échec du signalement de la publication',
  FAILED_TO_REPORT_COMMENT: 'Échec du signalement du commentaire',
  FAILED_TO_PUBLISH_DRAFT: 'Échec de la publication du brouillon',
  FAILED_TO_LOAD_EMOTIONS: 'Impossible de charger les émotions',
  FAILED_TO_LOAD_ACTIVITIES: 'Impossible de charger les activités',
  FAILED_TO_CREATE_EMOTION: 'Impossible de créer l\'émotion',
  FAILED_TO_CREATE_ACTIVITY: 'Impossible de créer l\'activité',
  POST_PINNED_SUCCESSFULLY: 'Publication épinglée avec succès',
  POST_UPDATED_SUCCESSFULLY: 'Publication mise à jour avec succès',
  ALBUM_CREATION_FAILED: 'Échec de la création de l\'album',
  ALBUM_TITLE_EXISTS: 'Un album avec ce titre existe déjà',
  CANT_GET_ALBUMS: 'Impossible de charger les albums',
  CANT_GET_ALBUM: 'Impossible de charger l\'album',
  CANT_DELETE_ALBUM: 'Impossible de supprimer l\'album',
  ALBUM_ACCESS_DENIED: 'Accès refusé à cet album',
  ALBUM_DELETE_ACCESS_DENIED: 'Accès refusé - Vous ne pouvez pas supprimer cet album',
  ALBUM_NOT_FOUND: 'Album introuvable',
  POST_ALREADY_IN_ALBUM: 'Ce post est déjà dans l\'album',
  ALBUM_POST_ACCESS_DENIED: 'Accès refusé',
  ALBUM_OR_POST_NOT_FOUND: 'Album ou post introuvable',
  CANT_ADD_POST_TO_ALBUM: 'Impossible d\'ajouter le post à l\'album',
  ALBUM_UPDATE_FAILED: 'Échec de la mise à jour de l\'album',
  ALBUM_UPDATE_ACCESS_DENIED: 'Accès refusé - Vous ne pouvez pas modifier cet album',
  ALBUM_UPDATE_NOT_FOUND: 'Album introuvable',
  CANT_GET_ALBUM_MEDIA: 'Impossible de charger les médias de l\'album',
  CANT_REMOVE_POST_FROM_ALBUM: 'Impossible de retirer le post de l\'album',
  ALBUM_POST_REMOVE_ACCESS_DENIED: 'Accès refusé - Vous ne pouvez pas retirer ce post de l\'album',
  // Upload-related error messages (French)
  UPLOAD_TIMEOUT: 'Le téléchargement a expiré. Veuillez vérifier votre connexion et réessayer.',
  FILE_TOO_LARGE: 'La taille du fichier dépasse la limite autorisée. Veuillez compresser vos fichiers ou télécharger des fichiers plus petits.',
  UPLOAD_PROCESSING_TIMEOUT: 'Le serveur met trop de temps à traiter votre téléchargement. Veuillez réessayer avec moins de fichiers ou des fichiers plus petits.',
  // Collection-related error messages (French)
  SERVICE_UNAVAILABLE: 'Service temporairement indisponible. Veuillez réessayer plus tard.',
  CANT_GET_COLLECTIONS: 'Impossible de charger les collections',
  COLLECTION_CREATION_FAILED: 'Échec de la création de la collection',
  FAILED_TO_SAVE_POST: 'Échec de l\'enregistrement du post dans la collection',
  CANT_GET_SAVED_POSTS: 'Impossible de charger les posts enregistrés',
  FAILED_TO_CHECK_SAVED_POST: 'Échec de la vérification si le post est enregistré',
  SAVED_POST_NOT_FOUND: 'Post enregistré introuvable',
  CANT_GET_SAVED_POST: 'Impossible de charger le post enregistré',
  FAILED_TO_UPDATE_SAVED_POST: 'Échec de la mise à jour du post enregistré',
  POST_UNSAVED_SUCCESS: 'Post désenregistré avec succès',
  FAILED_TO_UNSAVE_POST: 'Échec du désenregistrement du post',
  CANT_GET_COLLECTION_POSTS: 'Impossible de charger les posts de la collection',
  FAILED_TO_UPDATE_COLLECTION: 'Échec de la mise à jour de la collection',
  FAILED_TO_DELETE_COLLECTION: 'Échec de la suppression de la collection',
  // Event-related error messages (French)
  CANT_GET_EVENTS: 'Impossible de charger les événements',
  FAILED_TO_DELETE_EVENT: 'Échec de la suppression de l\'événement',
  CANT_GET_EVENT_POSTS: 'Impossible de charger les posts de l\'événement',
  CANT_GET_SUGGESTED_EVENTS: 'Impossible de charger les événements suggérés',
  FAILED_TO_UPDATE_ATTENDANCE: 'Échec de la mise à jour du statut de participation',
  FAILED_TO_LEAVE_EVENT: 'Échec de la sortie de l\'événement',
  FAILED_TO_INVITE_FRIENDS: 'Échec de l\'invitation des amis',
  CANT_GET_EVENT_ATTENDEES: 'Impossible de charger les participants de l\'événement',
  FAILED_TO_REPORT_EVENT: 'Échec de l\'envoi du signalement',
  FAILED_TO_ACCEPT_INVITATION: 'Échec de l\'acceptation de l\'invitation',
  FAILED_TO_DECLINE_INVITATION: 'Échec du refus de l\'invitation',
  FAILED_TO_CREATE_EVENT_POST: 'Échec de la création du post de l\'événement',
  // Education-related error messages (French)
  CANT_GET_EDUCATIONS: 'Impossible de charger les formations',
  FAILED_TO_CREATE_EDUCATION: 'Échec de la création de la formation',
  FAILED_TO_DELETE_EDUCATION: 'Échec de la suppression de la formation',
  FAILED_TO_EDIT_EDUCATION: 'Échec de la modification de la formation',
  BAD_REQUEST: 'Requête invalide - Veuillez vérifier que tous les champs requis sont remplis correctement',
  CONTEST_REQUIRED_MENTIONS: 'Votre participation nécessite de mentionner au moins {requiredMentions} ami(s).',
  CONTEST_COMMENT_NOT_ADMIN: 'Seul l\'auteur de la publication peut répondre aux commentaires du concours.',
  CONTEST_REQUIRE_LIKE: 'Vous devez aimer la publication avant de participer au concours.',
  CONTEST_CANNOT_UNLIKE: 'Vous ne pouvez pas retirer votre réaction tant que vous participez à ce concours.',
  CONTEST_ENDED: 'Ce concours est terminé. Les participations ne sont plus acceptées.',
  CONTEST_ALREADY_PARTICIPATED: 'Vous avez déjà participé à ce concours. Bonne chance !',
  CONTEST_POST_NOT_FOUND: 'Publication introuvable.',
  CONTEST_NOT_CONTEST_POST: 'Cette publication n\'est pas un concours.',
  CONTEST_NOT_ADMIN: 'Seul l\'administrateur de la page peut tirer au sort le gagnant.',
  CONTEST_NOT_ENDED: 'Le concours n\'est pas encore terminé.',
  CONTEST_ALREADY_DRAWN: 'Un gagnant a déjà été tiré au sort.',
  CONTEST_NO_PARTICIPANTS: 'Aucun participant trouvé pour ce concours.',
  CONTEST_ADMIN_NOT_ELIGIBLE: 'Les propriétaires et administrateurs de la page ne peuvent pas être gagnants.',
  CONTEST_INVALID_WINNER: 'L\'utilisateur sélectionné n\'est pas un participant valide de ce concours.',
  CONTEST_DRAW_DATE_REQUIRED: 'Veuillez sélectionner une date et une heure pour le tirage au sort.',
  CONTEST_DRAW_DATE_INVALID: 'La date du tirage est invalide.',
  CONTEST_DRAW_DATE_MUST_BE_AFTER_END: 'La date du tirage doit être égale ou postérieure à la fin des participations.',
  NOT_ALLOWED_TO_CREATE_CONTEST: 'Seul le propriétaire de la page peut créer des posts concours.',
  NOT_ALLOWED_TO_UPDATE_CONTEST_FIELDS: 'Seul le propriétaire de la page peut modifier les paramètres du concours.',
  CONTEST_INVALID_DRAW_MODE: 'Mode de tirage invalide. Utilisez RANDOM ou MOST_REACTED.',
};

// Arabic translations
export const POST_ERROR_MESSAGES_AR = {
  VERIFICATION_FAILED: 'فشل التحقق من الرمز',
  CANT_GET_FEED: 'تعذر تحميل الخلاصة',
  CANT_GET_POST: 'تعذر تحميل المنشور',
  CANT_GET_DRAFTS: 'تعذر تحميل المسودات',
  CANT_DELETE_POST: 'تعذر حذف المنشور',
  FAILED_TO_PIN_POST: 'فشل تثبيت المنشور',
  FAILED_TO_MARK_AS_DRAFT: 'فشل حفظ المسودة',
  FAILED_TO_UPDATE_POST: 'فشل تحديث المنشور',
  FAILED_TO_REPORT_POST: 'فشل الإبلاغ عن المنشور',
  FAILED_TO_REPORT_COMMENT: 'فشل الإبلاغ عن التعليق',
  FAILED_TO_PUBLISH_DRAFT: 'فشل نشر المسودة',
  FAILED_TO_LOAD_EMOTIONS: 'تعذر تحميل المشاعر',
  FAILED_TO_LOAD_ACTIVITIES: 'تعذر تحميل الأنشطة',
  FAILED_TO_CREATE_EMOTION: 'تعذر إنشاء المشاعر',
  FAILED_TO_CREATE_ACTIVITY: 'تعذر إنشاء النشاط',
  POST_PINNED_SUCCESSFULLY: 'تم تثبيت المنشور بنجاح',
  POST_UPDATED_SUCCESSFULLY: 'تم تحديث المنشور بنجاح',
  SERVICE_UNAVAILABLE: 'الخدمة غير متاحة مؤقتاً. يرجى المحاولة لاحقاً.',
  BAD_REQUEST: 'طلب غير صالح - يرجى التحقق من جميع الحقول المطلوبة',
  CONTEST_REQUIRED_MENTIONS: 'يجب ذكر {requiredMentions} صديق/أصدقاء على الأقل للمشاركة في المسابقة.',
  CONTEST_COMMENT_NOT_ADMIN: 'فقط صاحب الصفحة يمكنه الرد على تعليقات المسابقة.',
  CONTEST_REQUIRE_LIKE: 'يجب إبداء الإعجاب بالمنشور قبل المشاركة في المسابقة.',
  CONTEST_CANNOT_UNLIKE: 'لا يمكن إزالة إعجابك أثناء مشاركتك في هذه المسابقة.',
  CONTEST_ENDED: 'انتهت هذه المسابقة. لم يعد قبول المشاركات.',
  CONTEST_ALREADY_PARTICIPATED: 'لقد شاركت بالفعل في هذه المسابقة. حظاً موفقاً!',
  CONTEST_POST_NOT_FOUND: 'المنشور غير موجود.',
  CONTEST_NOT_CONTEST_POST: 'هذا المنشور ليس مسابقة.',
  CONTEST_NOT_ADMIN: 'فقط مدير الصفحة يمكنه اختيار الفائز.',
  CONTEST_NOT_ENDED: 'المسابقة لم تنته بعد.',
  CONTEST_ALREADY_DRAWN: 'تم اختيار الفائز مسبقاً.',
  CONTEST_NO_PARTICIPANTS: 'لم يتم العثور على مشاركين في هذه المسابقة.',
  CONTEST_ADMIN_NOT_ELIGIBLE: 'لا يمكن لمالكي الصفحة ومديريها أن يكونوا فائزين.',
  CONTEST_INVALID_WINNER: 'المستخدم المحدد ليس مشاركاً صالحاً في هذه المسابقة.',
  CONTEST_DRAW_DATE_REQUIRED: 'يرجى اختيار تاريخ ووقت السحب للمسابقة.',
  CONTEST_DRAW_DATE_INVALID: 'تاريخ السحب غير صالح.',
  CONTEST_DRAW_DATE_MUST_BE_AFTER_END: 'يجب أن يكون تاريخ السحب في أو بعد تاريخ انتهاء المشاركة.',
  NOT_ALLOWED_TO_CREATE_CONTEST: 'مالك الصفحة فقط يمكنه إنشاء منشورات المسابقات.',
  NOT_ALLOWED_TO_UPDATE_CONTEST_FIELDS: 'مالك الصفحة فقط يمكنه تعديل إعدادات المسابقة.',
  CONTEST_INVALID_DRAW_MODE: 'وضع السحب غير صالح. استخدم RANDOM أو MOST_REACTED.',
};

// Function to get translated error message based on current language (uses Vue I18n locale when not passed)
export const getTranslatedErrorMessage = (key, extra = {}, currentLanguage = getCurrentLocale()) => {
  const lang = (currentLanguage || getCurrentLocale() || 'en').toLowerCase();
  let template;
  if (lang === 'fr') {
    template = POST_ERROR_MESSAGES_FR[key] || POST_ERROR_MESSAGES[key];
  } else if (lang === 'ar') {
    template = POST_ERROR_MESSAGES_AR[key] || POST_ERROR_MESSAGES[key];
  } else {
    template = POST_ERROR_MESSAGES[key];
  }
  if (!template) {
    return lang === 'fr' ? 'Une erreur s\'est produite' : (lang === 'ar' ? 'حدث خطأ' : 'An error occurred');
  }
  // Simple template replacement for dynamic values, e.g. {requiredMentions}
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = extra?.[name];
    return v !== undefined && v !== null ? String(v) : '';
  });
};

const DEFAULT_HTTP_STATUS_ERROR_CODE_MAP = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'TOO_MANY_REQUESTS',
  500: 'SERVER_ERROR',
  503: 'SERVICE_UNAVAILABLE',
};

const toCode = (value = '') =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Returns a safe UI error code from an API error.
 * - Keeps known/public codes (or allowedCodes provided by caller)
 * - Falls back to status-based generic codes to avoid exposing backend raw messages
 */
export const getSafeApiErrorCode = (error, options = {}) => {
  const payload = error?.response?.data || {};
  const status = Number(error?.response?.status) || 0;
  const defaultCode = options.defaultCode || 'REQUEST_FAILED';

  const knownCodes = new Set([
    ...Object.keys(POST_ERROR_MESSAGES),
    ...(options.allowedCodes || []),
  ]);

  const candidate = toCode(payload?.code || payload?.message || payload?.error);
  if (candidate && knownCodes.has(candidate)) {
    return candidate;
  }

  if (status && DEFAULT_HTTP_STATUS_ERROR_CODE_MAP[status]) {
    return DEFAULT_HTTP_STATUS_ERROR_CODE_MAP[status];
  }

  return defaultCode;
};
